import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';

import { usePaymentMethods, useDeletePaymentMethod } from '@/lib/api/hooks';
import { cardLabel, cardExpiry } from '@/lib/payment-cards';
import { AddCardForm } from './AddCardForm';

/**
 * Payment-method confirm + switch for the cart checkout.
 *
 * Owns the saved-card list, the inline "Change" panel (select / remove / add a
 * new card). Reports the chosen PaymentMethod id up via `onSelect`; the cart
 * passes it to the purchase call. `enabled` gates the network fetch so we don't
 * load cards for an empty cart.
 */
export function PaymentMethodPicker({
  selectedPmId,
  onSelect,
  enabled = true,
  avoidPmId = null,
  onPaymentStepEntered,
  paymentEntryKey,
}: {
  selectedPmId: string | null;
  onSelect: (pmId: string | null) => void;
  enabled?: boolean;
  // A card that just failed: never pre-selected (the patient can still pick it).
  avoidPmId?: string | null;
  onPaymentStepEntered?: () => void;
  paymentEntryKey?: string | null;
}) {
  const { data, isLoading, isSuccess, isFetching, isError } = usePaymentMethods(enabled);
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const paymentRoot = useRef<HTMLDivElement>(null);
  const paymentReady = enabled && isSuccess && !isFetching && !isError;
  const capturedEntry = useRef(false);
  const entryCallback = useRef(onPaymentStepEntered);
  entryCallback.current = onPaymentStepEntered;
  const hasEntryCallback = Boolean(onPaymentStepEntered);
  useEffect(() => { capturedEntry.current = false; }, [enabled, paymentEntryKey]);
  useEffect(() => {
    const root = paymentRoot.current;
    if (!paymentReady || !root || !hasEntryCallback || capturedEntry.current) return;
    let active = true;
    const observer = new IntersectionObserver((entries) => {
      if (active && entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
        active = false;
        capturedEntry.current = true;
        entryCallback.current?.();
        observer.disconnect();
      }
    });
    observer.observe(root);
    return () => { active = false; observer.disconnect(); };
  }, [paymentReady, hasEntryCallback, paymentEntryKey]);
  const del = useDeletePaymentMethod();

  const cards = data?.payment_methods ?? [];
  const defaultId = data?.default_payment_method_id ?? null;

  // Initialize / repair the selection: prefer the customer's Stripe default,
  // else the first card. Re-runs if the selected card was removed. A card that
  // just failed is moved off once; if the patient picks it again, a later
  // refresh keeps their choice.
  const avoided = useRef<string | null>(null);
  useEffect(() => {
    if (!data) return;
    const justFailed = Boolean(avoidPmId) && avoided.current !== avoidPmId;
    avoided.current = avoidPmId;
    const stillValid =
      selectedPmId && cards.some((c) => c.id === selectedPmId) && !(justFailed && selectedPmId === avoidPmId);
    if (stillValid) return;
    const usable = (id: string | null | undefined) => (id && id !== avoidPmId ? id : null);
    onSelect(usable(defaultId) ?? cards.find((c) => c.id !== avoidPmId)?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, avoidPmId]);

  const selected = cards.find((c) => c.id === selectedPmId) ?? null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-10 mb-12 type-body-sm text-ink-secondary">
        <Loader2 size={14} className="animate-spin" /> Loading payment…
      </div>
    );
  }

  // No saved cards yet — lead with Add a card.
  if (cards.length === 0 && !adding) {
    return (
      <div ref={paymentRoot} className="mb-12">
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-8 type-body-sm text-ink hover:opacity-80"
        >
          <Plus size={14} /> Add a card to check out
        </button>
      </div>
    );
  }

  return (
    <div ref={paymentRoot} className="mb-12 border border-line-subtle">
      {/* Collapsed summary row */}
      {!expanded && !adding && (
        <div className="flex items-center justify-between gap-10 px-12 py-10">
          <div className="min-w-0">
            <div className="type-label-sm text-ink-muted">PAYMENT</div>
            <div className="type-body-sm text-ink truncate">
              {selected ? cardLabel(selected) : 'Select a card'}
              {selected && cardExpiry(selected) && (
                <span className="text-ink-muted">
                  {' '}
                  · {cardExpiry(selected)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="type-label-sm text-ink-muted hover:text-ink underline underline-offset-2 shrink-0"
          >
            Change
          </button>
        </div>
      )}

      {/* Expanded: choose / remove / add */}
      {expanded && !adding && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-12 py-8 bg-[var(--surface-sunken)] border-b border-line-subtle">
            <span className="type-label-sm text-ink-muted">PAYMENT METHOD</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="type-label-sm text-ink-muted hover:text-ink"
            >
              Done
            </button>
          </div>
          <ul className="flex flex-col">
            {cards.map((pm) => {
              const isSel = pm.id === selectedPmId;
              return (
                <li
                  key={pm.id}
                  className="flex items-center gap-10 px-12 py-10 border-b border-line-subtle"
                >
                  <label className="flex items-center gap-10 flex-1 min-w-0 cursor-pointer">
                    <input
                      type="radio"
                      name="payment-method"
                      checked={isSel}
                      onChange={() => onSelect(pm.id)}
                    />
                    <span className="min-w-0">
                      <span className="type-body-sm text-ink truncate">
                        {cardLabel(pm)}
                      </span>
                      {pm.id === defaultId && (
                        <span className="ml-8 type-label-sm text-ink-muted">
                          DEFAULT
                        </span>
                      )}
                    </span>
                  </label>
                  <button
                    type="button"
                    aria-label="Remove card"
                    disabled={del.isPending}
                    onClick={() => del.mutate(pm.id)}
                    className="text-ink-muted hover:text-danger disabled:opacity-50 shrink-0"
                  >
                    <X size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
          {del.isError && (
            <div role="alert" className="px-12 py-8 type-body-sm text-danger">
              Couldn't remove that card.
            </div>
          )}
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-8 px-12 py-10 type-body-sm text-ink hover:bg-[var(--surface-sunken)]"
          >
            <Plus size={14} /> Add new card
          </button>
        </div>
      )}

      {/* Add-card form */}
      {adding && (
        <AddCardForm
          onAdded={(pmId) => {
            setAdding(false);
            setExpanded(false);
            if (pmId) onSelect(pmId);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
}
